/**
 *
 * Team: TA37 - The Coders' Assembly
 * Epic: 5 - Quit Journey Outlook
 */

import React, { useState } from 'react';
import TopFactors from './TopFactors';
import './OutlookPanel.css';

const OutlookPanel = ({
  fourWeek,
  fiftyTwoWeek,
  sourceLabel,
  sourceUrl,
  riskLevel,
  topFactors,
}) => {
  // Local state for the collapsible explainer (AC 5.1.3 task 2).
  const [explanationOpen, setExplanationOpen] = useState(false);

  // Graceful absence - bail before rendering anything if either headline
  // figure is missing. The parent does not need to know about this.
  if (
    fourWeek === null || fourWeek === undefined ||
    fiftyTwoWeek === null || fiftyTwoWeek === undefined
  ) {
    return null;
  }

  // Coerce just in case pg-driver-style strings sneak through.
  const fourWeekNum = Number(fourWeek);
  const fiftyTwoWeekNum = Number(fiftyTwoWeek);

  if (Number.isNaN(fourWeekNum) || Number.isNaN(fiftyTwoWeekNum)) {
    return null;
  }

  // Format probabilities to 0 decimal places - these are population
  // estimates, the precision implied by "48.0%" vs "48%" is misleading.
  const fmt = (value) => `${Math.round(value)}%`;

  const toggleExplanation = () => setExplanationOpen((prev) => !prev);

  return (
    <section
      className="outlook-panel"
      aria-label="Quit journey outlook"
    >
      <header className="outlook-panel__header">
        <h2 className="outlook-panel__title">Your Quit Journey Outlook</h2>
        {riskLevel && (
          <p className="outlook-panel__subtitle">
            Population benchmark for people in the {riskLevel.toLowerCase()} risk group
          </p>
        )}
      </header>

      {/* -- Two headline figures (AC 5.1.1) -- */}
      <div className="outlook-panel__figures">
        <article className="outlook-panel__figure">
          <p className="outlook-panel__figure-value">{fmt(fourWeekNum)}</p>
          <p className="outlook-panel__figure-caption">
            of similar people were smoke free at <strong>four weeks</strong>
          </p>
        </article>

        <article className="outlook-panel__figure">
          <p className="outlook-panel__figure-value">{fmt(fiftyTwoWeekNum)}</p>
          <p className="outlook-panel__figure-caption">
            of similar people were smoke free at <strong>one year</strong>
          </p>
        </article>
      </div>

      <p className="outlook-panel__note">
        A score of 0 places you in the lowest risk group. These percentages are
        group outcomes, not a guarantee or a personal probability.
      </p>

      <TopFactors factors={topFactors} />

      {/* -- Citation (AC 5.1.1) -- */}
      <p className="outlook-panel__citation">
        Based on {sourceLabel || 'NHS England stop smoking services outcome data'}.
      </p>

      {/* -- Collapsible "How we calculated this" (AC 5.1.3) -- */}
      <div className="outlook-panel__explainer">
        <button
          type="button"
          className="outlook-panel__explainer-toggle"
          onClick={toggleExplanation}
          aria-expanded={explanationOpen}
          aria-controls="outlook-panel-explainer-body"
        >
          How we calculated this {explanationOpen ? '▴' : '▾'}
        </button>

        {explanationOpen && (
          <div
            id="outlook-panel-explainer-body"
            className="outlook-panel__explainer-body"
            role="region"
          >
            <p>
              These probabilities come from NHS England's published Stop
              Smoking Services outcome statistics, mapped onto QuitPact's
              three risk bands (Low, Medium, High) using the cohort's
              4-week CO-validated quit rates and 52-week sustained-abstinence
              follow-up data.
            </p>

            <p className="outlook-panel__disclaimer">
              <strong>This is population data, not a personal prediction.</strong>{' '}
              It tells you what tends to happen for people with a similar
              risk profile - not what will happen to you specifically. Many
              factors that affect an individual's quit attempt are not
              captured by a check-in score.
            </p>

            {sourceUrl && (
              <p className="outlook-panel__source-link-wrap">
                <a
                  href={sourceUrl}
                  className="outlook-panel__source-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the NHS source data →
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default OutlookPanel;
